import { useCallback, useMemo, useReducer } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { REJECTED_APPROVAL_ONLY_TO_CLOSED_LOST, RESTART_APPROVAL_WARNING } from '../utils/pipelineValidationTypes';
import { trackRejectedReviveDealSubmit } from '../../../utils/usageTrackingUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        isOpen: true,
        onConfirm: action.onConfirm,
        onCancel: action.onCancel,
        errorCode: action.errorCode
      };
    case 'CLOSE_MODAL':
      return {
        isOpen: false,
        onConfirm: emptyFunction,
        onCancel: emptyFunction
      };
    default:
      return state;
  }
};
const initialState = {
  isOpen: false,
  onConfirm: emptyFunction,
  onCancel: emptyFunction
};
const StageValidationModal = ({
  onConfirm,
  onCancel,
  errorCode = RESTART_APPROVAL_WARNING
}) => {
  const isReviveRejectedDeal = errorCode === REJECTED_APPROVAL_ONLY_TO_CLOSED_LOST;
  return /*#__PURE__*/_jsxs(Modal, {
    width: 450,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.stageValidation.modals.restartApproval.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.stageValidation.modals.restartApproval.body"
        })
      }), /*#__PURE__*/_jsxs(_Fragment, {
        children: [isReviveRejectedDeal && /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.stageValidation.modals.restartApproval.bypassAdmin"
          })
        }), /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.stageValidation.modals.restartApproval.bypassConfirm"
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: onConfirm,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.stageValidation.modals.restartApproval.footer.confirm"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.stageValidation.modals.restartApproval.footer.cancel"
        })
      })]
    })]
  });
};
export const useStageValidationModal = () => {
  const [modalState, dispatch] = useReducer(modalReducer, initialState);
  const openModal = useCallback((modalData, errorCode) => {
    const wrappedOnConfirm = () => {
      if (errorCode === REJECTED_APPROVAL_ONLY_TO_CLOSED_LOST) {
        trackRejectedReviveDealSubmit();
      }
      modalData.onConfirm();
    };
    dispatch({
      type: 'OPEN_MODAL',
      onConfirm: wrappedOnConfirm,
      onCancel: modalData.onCancel,
      errorCode
    });
  }, [dispatch]);
  const closeModal = useCallback(() => {
    dispatch({
      type: 'CLOSE_MODAL'
    });
  }, [dispatch]);
  const stageValidationModal = useMemo(() => {
    if (!modalState.isOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx(StageValidationModal, {
      onConfirm: modalState.onConfirm,
      onCancel: modalState.onCancel,
      errorCode: modalState.errorCode
    });
  }, [modalState]);
  return useMemo(() => ({
    openModal,
    closeModal,
    stageValidationModal
  }), [openModal, closeModal, stageValidationModal]);
};